import { useEffect, useState, useRef } from "react";

const UNIT_MS = {
	minute: 60 * 1000,
	hour: 60 * 60 * 1000,
	day: 24 * 60 * 60 * 1000,
	week: 7 * 24 * 60 * 60 * 1000,
};

/**
 * Mirrors the library's drop maths: a committed move shifts the task by
 * Math.round(dx / lengthUnitWidth) whole length units, so the preview snaps
 * exactly where the bar will land, not where the pointer hovers.
 */
export const getDragPreviewDates = (snapshot, left, scales) => {
	const unitWidth = scales?.lengthUnitWidth;
	const unitMs = UNIT_MS[scales?.lengthUnit];
	if (!snapshot || !unitWidth || !unitMs) return null;

	const shiftMs = Math.round((left - snapshot.x) / unitWidth) * unitMs;
	return {
		start: new Date(snapshot.start.getTime() + shiftMs),
		end: new Date(snapshot.end.getTime() + shiftMs),
	};
};

const useGanttDragDatePreview = ({ ganttApi, containerRef }) => {
	const [preview, setPreview] = useState(null);
	const snapshotRef = useRef(null);
	const dragActiveRef = useRef(false);

	useEffect(() => {
		if (!ganttApi) return undefined;

		// The interceptor runs before the store moves the bar, which is the only
		// point where the task still carries its pre-drag position
		const unsubscribeSnapshot = ganttApi.intercept("drag-task", (ev) => {
			if (!ev.inProgress) {
				snapshotRef.current = null;
				return true;
			}
			if (snapshotRef.current?.id !== ev.id) {
				const task = ganttApi.getTask(ev.id);
				if (task?.start && task?.end) {
					snapshotRef.current = {
						id: ev.id,
						start: task.start,
						end: task.end,
						x: task.$x,
					};
				}
			}
			return true;
		});

		const unsubscribeDrag = ganttApi.on("drag-task", (ev) => {
			if (!ev.inProgress) {
				dragActiveRef.current = false;
				setPreview(null);
				return;
			}
			if (typeof ev.left !== "number") return;
			dragActiveRef.current = true;

			const dates = getDragPreviewDates(
				snapshotRef.current,
				ev.left,
				ganttApi.getState()._scales
			);
			if (!dates) return;

			// The store has updated the bar's position but React has not painted
			// it yet; measure after the next frame so the overlay tracks the bar
			requestAnimationFrame(() => {
				if (!dragActiveRef.current) return;
				const container = containerRef.current;
				const bar = container?.querySelector(`.wx-bar[data-id="${ev.id}"]`);
				if (!bar) return;

				const containerRect = container.getBoundingClientRect();
				const barRect = bar.getBoundingClientRect();
				setPreview({
					...dates,
					left: barRect.left - containerRect.left,
					right: barRect.right - containerRect.left,
					top: barRect.top - containerRect.top,
					height: barRect.height,
					containerWidth: containerRect.width,
				});
			});
		});

		// A committed move ends with update-task only; the library sends the
		// closing drag-task event just for drags that land back where they began
		const unsubscribeUpdate = ganttApi.on("update-task", () => {
			if (!dragActiveRef.current) return;
			dragActiveRef.current = false;
			snapshotRef.current = null;
			setPreview(null);
		});

		// intercept() registrations return no unsubscribe on this api
		return () => {
			[unsubscribeSnapshot, unsubscribeDrag, unsubscribeUpdate].forEach(
				(cleanup) => {
					if (typeof cleanup === "function") cleanup();
				}
			);
		};
	}, [ganttApi, containerRef]);

	return preview;
};

export default useGanttDragDatePreview;
